import React, { createContext, useCallback, useContext, useMemo } from 'react';

import type { fetchJSON } from './utils/request';

export type InFlightRequestValue = ReturnType<typeof fetchJSON> | boolean;

type UniversalCreateBFFContext = {
	/** Map of request keys to their in-flight status (boolean) or stored promise (for deduplication) */
	inFlightRequests: Map<string, InFlightRequestValue>;
	setInFlightRequests: (requestKey: string, value: InFlightRequestValue) => void;
};

export const UniversalCreateBFFContext: React.Context<UniversalCreateBFFContext> = createContext<UniversalCreateBFFContext>({
	inFlightRequests: new Map(),
	setInFlightRequests: () => {},
});

export function useUniversalCreateBFFContext(): UniversalCreateBFFContext {
	return useContext(UniversalCreateBFFContext);
}

export function UniversalCreateBFFProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
	const inFlightRequestsRef = React.useRef<UniversalCreateBFFContext['inFlightRequests']>(
		new Map(),
	);

	const setInFlightRequests = useCallback((requestKey: string, value: InFlightRequestValue) => {
		inFlightRequestsRef.current.set(requestKey, value);
	}, []);

	const contextValue = useMemo(
		() => ({
			inFlightRequests: inFlightRequestsRef.current,
			setInFlightRequests,
		}),
		[setInFlightRequests],
	);

	return (
		<UniversalCreateBFFContext.Provider value={contextValue}>
			{children}
		</UniversalCreateBFFContext.Provider>
	);
}
